import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { InterfaceService } from '@app/interfaces';

export interface TaskPhoto {
    id: number;
    uuid: string;
    name: string;
    url: string;
    location: string;
    type: string;
}

export interface TaskTag {
    id: number;
    name: string;
    colour: string;
}

export interface TaskAsset {
    assetId: number;
    asset_identifiers: string;
    displayName: string;
    groupTypeDescription: string;
}

export interface OnsiteTask {
    id: number;
    description: string;
    description2?: string;
    type: 'photo' | 'checkbox' | 'signature';
    status: number; // 0: incomplete, 1: completed, 2: skipped
    photos: TaskPhoto[];
    tags: TaskTag[];
    assets?: TaskAsset[];
    tablePkid: number;
    tableType: string;
    skipTask?: boolean;
    feedback?: string;
}

export interface DiaryTasksResponse {
    tasks: OnsiteTask[];
    total: number;
    counts: {
        total: number;
        incomplete: number;
        completed: number;
        skipped: number;
    };
}

@Injectable({
    providedIn: 'root'
})
export class DiaryTasksService extends InterfaceService {

    constructor(
        private http: HttpClient
    ) {
        super();
    }

    /**
     * Fetch onsite tasks for a specific diary event
     * @param diaryId - The diary event ID
     * @param status - Filter by status: 'incomplete', 'completed', 'skipped', or null for all
     * @param searchTerm - Optional search term to filter tasks
     */
    getOnsiteTasks(diaryId: number, status?: string, searchTerm?: string): Observable<DiaryTasksResponse> {
        let params = new HttpParams();

        if (status !== undefined && status !== null) {
            params = params.set('status', status);
        }

        if (searchTerm && searchTerm.trim()) {
            params = params.set('search', searchTerm.trim());
        }

        return this.http.get<DiaryTasksResponse>(
            this.getJobApiUrl(`diary/${diaryId}/onSiteTasks`),
            { params }
        );
    }

    /**
     * Get tasks filtered by status
     */
    getTasksByStatus(diaryId: number, status: 'incomplete' | 'completed' | 'skipped', searchTerm?: string): Observable<DiaryTasksResponse> {
        return this.getOnsiteTasks(diaryId, status, searchTerm);
    }

    /**
     * Delete an on-site task (soft delete)
     * @param diaryId - The diary event ID
     * @param taskId - The on-site task ID
     */
    deleteOnsiteTask(diaryId: number, taskId: number): Observable<any> {
        return this.http.delete(
            this.getJobApiUrl(`diary/${diaryId}/onSiteTasks/${taskId}/delete`)
        );
    }
}
